import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  styled,
  Grid,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import JsFileDownloader from 'js-file-download';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  downloadImportDefinitionCsv,
  downloadImportedCsvReport,
  getImportedCsvReportsData,
  importCsvFile,
} from '../../services/import-export.service';
import ListImportExportData from '../form-elements/list-import-export-data.component';
import Iconify from '../common/iconify.component';
import validateCsvFile from '../../utils/file-validations.util';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ListImport = () => {
  const [options, setOptions] = useState({
    rows: [],
    reloadCounter: 0,
    loading: true,
    error: false,
    downloadingFiles: [],
    reportsData: {},
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [processing, setProccessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const selectFileInputRef = useRef(null);

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleImportFile = () => {
    setProccessing(true);
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const validationError = validateCsvFile(selectedFile);
    if (validationError) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: validationError,
        });
      }, 50);
      setProccessing(false);
    } else {
      const importFilePayload = {
        file: selectedFile[0],
      };
      importCsvFile(importFilePayload)
        .then((res) => {
          if (res.data.success) {
            setSnackbarInfo({
              ...snackbarInfo,
              show: true,
              type: 'success',
              message: 'File imported successfully.',
            });
            setSelectedFile(null);
            setProccessing(false);
            handleRefreshData();
          }
        })
        .catch((err) => {
          const message =
            err.response?.data?.message ||
            'Something went wrong, please try again.';

          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'error',
            message,
          });
          setProccessing(false);
        });
    }
  };

  const handleDownloadFile = (file) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });
    setOptions((prevOpt) => ({
      ...prevOpt,
      downloadingFiles: [...prevOpt.downloadingFiles, file.id],
    }));

    downloadImportedCsvReport(file.id)
      .then((res) => {
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(res, `${file.downloadFileName}`);
        setOptions((prevOpt) => ({
          ...prevOpt,
          downloadingFiles: prevOpt.downloadingFiles.filter(
            (df) => df !== file.id
          ),
        }));
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setOptions((prevOpt) => ({
          ...prevOpt,
          downloadingFiles: prevOpt.downloadingFiles.filter(
            (df) => df !== file.id
          ),
        }));
      });
  };

  const handleDefinitionFile = () => {
    setProccessing(true);

    downloadImportDefinitionCsv()
      .then((res) => {
        // eslint-disable-next-line no-unused-vars
        const download = new JsFileDownloader(
          res,
          'import-definition-file.csv'
        );
        setProccessing(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProccessing(false);
      });
  };

  useEffect(() => {
    getImportedCsvReportsData()
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          rows: res.data?.importedData || [],
          reportsData: res.data,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Stack sx={{ py: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardHeader
              title="Import Metadata CSV"
              subheader="Imported CSV will be loaded into the database and will be accessible thorugh bulk-editor. Importer will do the following:"
              action={
                <Button
                  variant="contained"
                  color="inherit"
                  size="small"
                  startIcon={<Iconify icon="ic:baseline-download" />}
                  onClick={() => handleDefinitionFile()}
                  disabled={processing}
                >
                  Definition File
                </Button>
              }
            />

            <Box paddingX={5} paddingBottom={3} paddingTop={1}>
              <Typography variant="body2">
                Create series/distributors/playlist objects
              </Typography>
              <Typography variant="body2">
                NOT Check for valid video/poster/subtitle content
              </Typography>
              <Typography variant="body2">
                NOT crawl IMDB automatically
              </Typography>
            </Box>

            <Divider />

            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                border="1px solid #ced4da"
                borderRadius="6px"
                sx={{ cursor: processing ? 'default' : 'pointer' }}
                onClick={() =>
                  selectFileInputRef.current &&
                  selectFileInputRef.current.click()
                }
                onChange={(e) => setSelectedFile(e.target.files)}
              >
                <Typography variant="body2" color="#637381" paddingLeft={2}>
                  {selectedFile?.[0]?.name || 'Choose CSV File'}
                </Typography>

                <Button
                  component="label"
                  variant="contained"
                  color="secondary"
                  disabled={processing}
                >
                  Browse
                </Button>
                <VisuallyHiddenInput
                  type="file"
                  ref={selectFileInputRef}
                  disabled={processing}
                />
              </Box>

              <Button
                component="label"
                variant="contained"
                color="primary"
                disabled={!selectedFile || processing}
                onClick={() => handleImportFile()}
              >
                Import
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={6}>
          <Card>
            <CardHeader
              title="Import Metadata Reports"
              action={
                <IconButton
                  title="Refresh"
                  onClick={handleRefreshData}
                  disabled={options.loading}
                >
                  <Iconify icon="ic:twotone-refresh" />
                </IconButton>
              }
              sx={{ paddingBottom: 3 }}
            />

            <Divider />

            <CardContent>
              <Box pb={2} px={1}>
                <Typography variant="subtitle1" color="#637381">
                  {`Imported Videos - ${options.reportsData.importedRows}`}
                </Typography>
                <Typography variant="subtitle1" color="#637381">
                  {`Failed Importing - ${options.reportsData.failedRows}`}
                </Typography>
              </Box>

              <Box px={1}>
                <ListImportExportData
                  rows={options.rows}
                  loading={options.loading}
                  error={options.error}
                  handleDownloadFile={handleDownloadFile}
                  fromImport
                  disabledDownload={false}
                  downloadingFiles={options.downloadingFiles}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Stack>
  );
};

export default ListImport;
